import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import ArialNormal from "../../../../utils/Customfonts/ARIAL.TTF";
import ArialBold from "../../../../utils/Customfonts/ARIALBD.TTF";
import ArialItalic from "../../../../utils/Customfonts/ARIALI.TTF";
import ArialBoldItalic from "../../../../utils/Customfonts/ARIALBI.TTF";

const DYC_LOGO = "/img/dycgpl-logo.webp";

export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "Arial",
  normalFont: "Arial",
  italic: "italic",
  italicFont: "Arial",
};

// -----------------------------------------------------------------------------
// GLOBAL LAYOUT CONFIG (Following Senior Developer CV Pattern)
// -----------------------------------------------------------------------------

export const config = {
  borderMargin: 15,
  contentPadding: 20,

  get margin() {
    return this.borderMargin + this.contentPadding;
  },

  fontSize: 8.8,
  fontSizeSm: 7.8,
  fontSizeLg: 10.5,
  fontSizeTitle: 14,

  cellPadding: 4.5,
  sectionGap: 12,
  lineGap: 14,

  headerFontSize: 8.5,
  headerFont: customFontFamily.boldFont,
  headerFontStyle: customFontFamily.bold,
  headerAlign: "left",
  headerPadding: { top: 5, right: 4, bottom: 5, left: 4 },

  get contentTop() {
    return this.borderMargin + 65;
  },

  get contentBottom() {
    return this.borderMargin + 25;
  },
};

// -----------------------------------------------------------------------------
// COMMON STYLES
// -----------------------------------------------------------------------------

const getBaseStyles = () => ({
  fontSize: config.fontSizeSm,
  cellPadding: config.cellPadding,
  lineColor: [0, 0, 0],
  lineWidth: 0.5,
  textColor: [0, 0, 0],
  fillColor: false,
  font: customFontFamily.normalFont,
  fontStyle: customFontFamily.normal,
  halign: "left",
  valign: "middle",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font: customFontFamily.boldFont,
  fontStyle: customFontFamily.bold,
  fillColor: [225, 225, 225],
  textColor: [0, 0, 0],
  halign: config.headerAlign,
  valign: "middle",
});

// -----------------------------------------------------------------------------
// LAYOUT MANAGER (Single Source of Truth for Y position & page breaks)
// -----------------------------------------------------------------------------

const layout = {
  doc: null,
  y: 0,
  pageWidth: 0,
  pageHeight: 0,
  contentWidth: 0,

  init(doc) {
    this.doc = doc;
    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - config.margin * 2;
    this.y = config.contentTop;
  },

  advance(amount) {
    this.y += amount;
  },

  ensureSpace(requiredHeight = 40) {
    if (this.y + requiredHeight > this.pageHeight - config.contentBottom) {
      this.doc.addPage();
      this.y = config.contentTop;
    }
  },

  afterTable(gap = config.sectionGap) {
    if (this.doc.lastAutoTable) {
      this.y = this.doc.lastAutoTable.finalY + gap;
    }
  },
};

// -----------------------------------------------------------------------------
// FONT LOADER
// -----------------------------------------------------------------------------

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  try {
    const response = await fetch(fontUrl);
    const buffer = await response.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);

    pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, base64);
    pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
  } catch (error) {
    console.error(`Font loading failed: ${fontName}`, error);
  }
};

// -----------------------------------------------------------------------------
// PAGE DECORATORS (Outer Border, Header Line, Logo, Footers on EVERY page)
// -----------------------------------------------------------------------------

const addPageDecorators = (doc, totalPages, data = {}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);

    // 1. OUTER BORDER
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.7);
    doc.rect(
      config.borderMargin,
      config.borderMargin,
      pageWidth - config.borderMargin * 2,
      pageHeight - config.borderMargin * 2
    );

    // 2. HEADER TITLE (Centered)
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(14);
    doc.setTextColor(20, 20, 20);
    doc.text(
      "INTERVIEW EVALUATION OF PERSONNEL FORM",
      pageWidth / 2,
      config.borderMargin + 32,
      { align: "center" }
    );

    // 3. LOGO (Top Right)
    try {
      const logoSize = 42;
      const logoX = pageWidth - config.borderMargin - logoSize - 8;
      const logoY = config.borderMargin + 5;

      try {
        doc.addImage(DYC_LOGO, "WEBP", logoX, logoY, logoSize, logoSize);
      } catch (_) {
        try {
          doc.addImage(DYC_LOGO, "PNG", logoX, logoY, logoSize, logoSize);
        } catch (__) {
          doc.addImage(DYC_LOGO, logoX, logoY, logoSize, logoSize);
        }
      }
    } catch (_) {
      // Optional logo
    }

    // 4. HEADER LINE
    doc.setLineWidth(0.5);
    doc.line(
      config.borderMargin,
      config.borderMargin + 55,
      pageWidth - config.borderMargin,
      config.borderMargin + 55
    );

    // 5. FOOTER (2 Dynamic Lines + Page Count)
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(7.5);
    doc.setTextColor(40, 40, 40);

    const docCode = `${data.docCode || "DYC/FMT-MR/21"} Rev.: ${data.revNo || "00"}, Eff.Dated -${data.effDate || "24/05/2023"}`;
    const footerY1 = pageHeight - config.borderMargin - 14;
    const footerY2 = pageHeight - config.borderMargin - 5;

    // Line 1 Left
    doc.text(docCode, config.borderMargin + 8, footerY1);

    // Line 2 Left
    doc.text(
      "Company General Use. All Rights Reserved by DYC Global Pvt Ltd.",
      config.borderMargin + 8,
      footerY2
    );

    // Right Page Number
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.text(`Page ${page} of ${totalPages}`, pageWidth - config.borderMargin - 60, footerY1);
  }
};

// -----------------------------------------------------------------------------
// SECTION 1: CANDIDATE INFO & CV ASSESSMENT
// -----------------------------------------------------------------------------

const drawSection1 = (doc, data) => {
  const contentW = layout.contentWidth;
  const leftX = config.margin;

  // Candidate Details Bar
  const candidateRows = [
    [
      {
        content: `Name of candidate: ${data.candidateName || ""}`,
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, cellWidth: contentW * 0.45 },
      },
      {
        content: `Location: ${data.location || ""}`,
        styles: { cellWidth: contentW * 0.28 },
      },
      {
        content: `Date of Evaluation: ${data.evaluationDate || ""}`,
        styles: { cellWidth: contentW * 0.27 },
      },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    body: candidateRows,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSizeSm, fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: contentW * 0.45 },
      1: { cellWidth: contentW * 0.28 },
      2: { cellWidth: contentW * 0.27 },
    },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(8);

  // Section 1 Table
  const totalSec1 =
    (Number(data.educationScore) || 0) +
    (Number(data.experienceScore) || 0) +
    (Number(data.certificationScore) || 0);

  const sec1Rows = [
    [
      {
        content: "SECTION 1 – CV Assessment for basic competence",
        colSpan: 6,
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [225, 235, 250], fontSize: 9 },
      },
    ],
    [
      { content: "1.", styles: { halign: "center" } },
      { content: "Basic Education – 10 points", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: "• Diploma or equivalent (Full time): 5 pts\n• Full time diploma + B.E/B.Tech (part time): 7 pts\n• B.E/B.Tech (Full time): 9 pts\n• M.S/M.E/M.Tech/Chartered engineer in relevant stream: 10 pts" },
      { content: "10", styles: { halign: "center" } },
      { content: "5", styles: { halign: "center" } },
      { content: String(data.educationScore || "0"), styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
    ],
    [
      { content: "2.", styles: { halign: "center" } },
      { content: "Total experience – 10 points (Mech) / 20 points (Other)", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: "• Only shopfloor QC / Site QC exp (1-4 yrs) OR Only TPI exp (1-4 yrs): 5 (Mech) / 10 (Other)\n• 5 - 9 years Shopfloor QC or TPI: 7 (Mech) / 14 (Other)\n• >10 years (Shopfloor QC or TPI): 9 (Mech) / 18 (Other)\n• >10 years (Shopfloor QC or TPI + Design): 10 (Mech) / 20 (Other)" },
      { content: "10 / 20", styles: { halign: "center" } },
      { content: "5 / 10", styles: { halign: "center" } },
      { content: String(data.experienceScore || "0"), styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
    ],
    [
      { content: "3.", styles: { halign: "center" } },
      { content: "Additional certifications – 10 points (Mech Only)", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: "• NDE Level II OR Other Specialization: 5 pts\n• NDE level II + Welding (CWI/CSWIP): 7 pts\n• NDE Level II + Welding (CWI/CSWIP) + Painting (NACE/BGAS): 10 pts" },
      { content: "10", styles: { halign: "center" } },
      { content: "5", styles: { halign: "center" } },
      { content: String(data.certificationScore || "0"), styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
    ],
    [
      {
        content: "Total Score (Maximum: 30, Minimum Required: 15):",
        colSpan: 3,
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, halign: "right" },
      },
      { content: "30", styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: "15", styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: String(totalSec1), styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    head: [
      [
        { content: "Sr.", styles: { halign: "center" } },
        { content: "Title" },
        { content: "Criteria / Breakdown" },
        { content: "Max points", styles: { halign: "center" } },
        { content: "Min Points", styles: { halign: "center" } },
        { content: "Candidate Score", styles: { halign: "center" } },
      ],
    ],
    body: sec1Rows,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSizeSm },
    headStyles: getHeaderStyles(),
    columnStyles: {
      0: { cellWidth: contentW * 0.06, halign: "center" },
      1: { cellWidth: contentW * 0.28 },
      2: { cellWidth: contentW * 0.42 },
      3: { cellWidth: contentW * 0.08, halign: "center" },
      4: { cellWidth: contentW * 0.08, halign: "center" },
      5: { cellWidth: contentW * 0.08, halign: "center" },
    },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(10);
};

// -----------------------------------------------------------------------------
// SECTION 2: GENERAL EVALUATION
// -----------------------------------------------------------------------------

const drawSection2 = (doc, data) => {
  const contentW = layout.contentWidth;
  const leftX = config.margin;

  const sec2List = data.sec2Characteristics || [
    { id: 1, label: "Appearance", maxPoints: 2, score: "", comments: "" },
    { id: 2, label: "Potential for long term Association", maxPoints: 4, score: "", comments: "" },
    { id: 3, label: "Family background", maxPoints: 2, score: "", comments: "" },
    { id: 4, label: "Manners & Etiquettes", maxPoints: 4, score: "", comments: "" },
    { id: 5, label: "Verbal Communication Skills", maxPoints: 4, score: "", comments: "" },
    { id: 6, label: "Written Communication Skills", maxPoints: 4, score: "", comments: "" },
  ];

  const totalSec2 = sec2List.reduce((acc, curr) => acc + (Number(curr.score) || 0), 0);

  const sec2TableRows = [
    [
      {
        content: "SECTION 2 – General",
        colSpan: 4,
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [225, 235, 250], fontSize: 9 },
      },
    ],
    ...sec2List.map((item) => [
      { content: item.label, styles: { font: customFontFamily.normalFont, fontStyle: customFontFamily.normal } },
      { content: String(item.maxPoints), styles: { halign: "center" } },
      { content: String(item.score || "-"), styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: item.comments || "" },
    ]),
    [
      {
        content: "Total Score (Maximum 20):",
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, halign: "right" },
      },
      { content: "20", styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: String(totalSec2), styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: totalSec2 >= 12 ? "Passed General Assessment" : "Needs Review", styles: { font: customFontFamily.italicFont, fontStyle: customFontFamily.italic } },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    head: [
      [
        { content: "Characteristic" },
        { content: "Max points", styles: { halign: "center" } },
        { content: "Candidate Score", styles: { halign: "center" } },
        { content: "Comments (If any)" },
      ],
    ],
    body: sec2TableRows,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSizeSm },
    headStyles: getHeaderStyles(),
    columnStyles: {
      0: { cellWidth: contentW * 0.44 },
      1: { cellWidth: contentW * 0.12, halign: "center" },
      2: { cellWidth: contentW * 0.14, halign: "center" },
      3: { cellWidth: contentW * 0.30 },
    },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(10);
};

// -----------------------------------------------------------------------------
// SECTION 3: TECHNICAL EVALUATION
// -----------------------------------------------------------------------------

const drawSection3 = (doc, data) => {
  const contentW = layout.contentWidth;
  const leftX = config.margin;

  const techList = data.technicalTopics || [];
  const sec3Rows = [
    [
      {
        content: "SECTION 3 – Technical evaluation\n(For details of products, refer DYC/FMT-MR/17 | Strike out whichever is not applicable)",
        colSpan: 5,
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [225, 235, 250], fontSize: 9 },
      },
    ],
  ];

  techList.forEach((topic, idx) => {
    const showScopeHeader = idx === 0 || topic.scope !== techList[idx - 1].scope;
    if (showScopeHeader) {
      sec3Rows.push([
        {
          content: topic.scope,
          colSpan: 5,
          styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [240, 245, 255] },
        },
      ]);
    }
    sec3Rows.push([
      { content: topic.name },
      { content: topic.stream, styles: { halign: "center" } },
      { content: topic.evaluated ? "[ ✓ ]" : "[   ]", styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: topic.evaluated ? String(topic.score || "-") : "-", styles: { halign: "center" } },
      { content: topic.comments || "" },
    ]);
  });

  sec3Rows.push([
    {
      content: "Overall Rating out of 50 points for evaluated topics:",
      colSpan: 3,
      styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, halign: "right" },
    },
    {
      content: String(data.overallTechnicalScore || "-"),
      styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold },
    },
    {
      content: data.overallTechnicalComments || "",
      styles: { font: customFontFamily.italicFont, fontStyle: customFontFamily.italic },
    },
  ]);

  autoTable(doc, {
    startY: layout.y,
    head: [
      [
        { content: "Inspection Activity / Knowledge" },
        { content: "Applicable for Stream", styles: { halign: "center" } },
        { content: "Put (✓) mark if evaluated", styles: { halign: "center" } },
        { content: "Evaluation (scale 1 - 10)", styles: { halign: "center" } },
        { content: "Comments (If any)" },
      ],
    ],
    body: sec3Rows,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSizeSm },
    headStyles: getHeaderStyles(),
    columnStyles: {
      0: { cellWidth: contentW * 0.44 },
      1: { cellWidth: contentW * 0.14, halign: "center" },
      2: { cellWidth: contentW * 0.12, halign: "center" },
      3: { cellWidth: contentW * 0.12, halign: "center" },
      4: { cellWidth: contentW * 0.18 },
    },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(10);
};

// -----------------------------------------------------------------------------
// RESULTS & RECOMMENDED LEVEL OF AUTHORIZATION
// -----------------------------------------------------------------------------

const drawResultsAndAuthorizations = (doc, data) => {
  const contentW = layout.contentWidth;
  const leftX = config.margin;

  // Intro text
  const resultsIntro = [
    [
      {
        content: "Results:\nAfter assessing technical questions in the given areas, I believe the candidate/inspector can comprehend and apply the specified requirements. This makes them capable of performing inspections at the mentioned levels/categories.",
        styles: { font: customFontFamily.italicFont, fontStyle: customFontFamily.italic, fontSize: config.fontSizeSm, fillColor: [248, 250, 252] },
      },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    body: resultsIntro,
    theme: "plain",
    styles: { ...getBaseStyles() },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(6);

  // Table 1: NABCB Scope Sector
  const nabcb = data.nabcbGrades || {};
  const nabcbBody = [
    [
      { content: "Recommended level of authorization (Grade-A/B/C/D)", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [245, 245, 245] } },
      { content: nabcb.scope17 || "-", styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: nabcb.scope18 || "-", styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: nabcb.scope19 || "-", styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: nabcb.scope26 || "-", styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: nabcb.scope28 || "-", styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: nabcb.scope31 || "-", styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
      { content: nabcb.scope34 || "-", styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    head: [
      [
        { content: "NABCB Scope Sector", styles: { halign: "center" } },
        { content: "17\n(Basic metals)" },
        { content: "18\n(Machinery)" },
        { content: "19\n(Electrical)" },
        { content: "26\n(Gas supply)" },
        { content: "28\n(Building)" },
        { content: "31\n(Petroleum)" },
        { content: "34\n(ERDMP)" },
      ],
    ],
    body: nabcbBody,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSizeSm },
    headStyles: { ...getHeaderStyles(), halign: "center" },
    columnStyles: {
      0: { cellWidth: contentW * 0.23 },
      1: { cellWidth: contentW * 0.11, halign: "center" },
      2: { cellWidth: contentW * 0.11, halign: "center" },
      3: { cellWidth: contentW * 0.11, halign: "center" },
      4: { cellWidth: contentW * 0.11, halign: "center" },
      5: { cellWidth: contentW * 0.11, halign: "center" },
      6: { cellWidth: contentW * 0.11, halign: "center" },
      7: { cellWidth: contentW * 0.11, halign: "center" },
    },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(6);

  // Table 2: General Scope & NDE Mechanical
  const nde = data.ndeMechGrades || {};
  const ndeBody = [
    [
      { content: "Recommended level of authorization (Grade-A/B/C/D)", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [245, 245, 245] } },
      { content: nde.qa || "-", styles: { halign: "center" } },
      { content: nde.lab || "-", styles: { halign: "center" } },
      { content: nde.welding || "-", styles: { halign: "center" } },
      { content: nde.rtfi || "-", styles: { halign: "center" } },
      { content: nde.ut || "-", styles: { halign: "center" } },
      { content: nde.pt || "-", styles: { halign: "center" } },
      { content: nde.mt || "-", styles: { halign: "center" } },
      { content: nde.vt || "-", styles: { halign: "center" } },
      { content: nde.lt || "-", styles: { halign: "center" } },
      { content: nde.ect || "-", styles: { halign: "center" } },
      { content: nde.vbt || "-", styles: { halign: "center" } },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    head: [
      [
        { content: "General Scope", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
        { content: "Quality assurance", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
        { content: "Lab tests", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
        { content: "Welding", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
        { content: "NDE Mechanical", colSpan: 8, styles: { halign: "center" } },
      ],
      [
        { content: "RTFI", styles: { halign: "center" } },
        { content: "UT", styles: { halign: "center" } },
        { content: "PT", styles: { halign: "center" } },
        { content: "MT", styles: { halign: "center" } },
        { content: "VT", styles: { halign: "center" } },
        { content: "LT", styles: { halign: "center" } },
        { content: "ECT", styles: { halign: "center" } },
        { content: "VBT", styles: { halign: "center" } },
      ],
    ],
    body: ndeBody,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSizeSm },
    headStyles: { ...getHeaderStyles(), halign: "center" },
    columnStyles: {
      0: { cellWidth: contentW * 0.20 },
      1: { cellWidth: contentW * 0.09, halign: "center" },
      2: { cellWidth: contentW * 0.07, halign: "center" },
      3: { cellWidth: contentW * 0.08, halign: "center" },
      4: { cellWidth: contentW * 0.07, halign: "center" },
      5: { cellWidth: contentW * 0.07, halign: "center" },
      6: { cellWidth: contentW * 0.07, halign: "center" },
      7: { cellWidth: contentW * 0.07, halign: "center" },
      8: { cellWidth: contentW * 0.07, halign: "center" },
      9: { cellWidth: contentW * 0.07, halign: "center" },
      10: { cellWidth: contentW * 0.07, halign: "center" },
      11: { cellWidth: contentW * 0.07, halign: "center" },
    },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(6);

  // Table 3: Test On Electrical
  const elec = data.elecGrades || {};
  const elecBody = [
    [
      { content: "Recommended level of authorization (Grade-A/B/C/D)", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [245, 245, 245] } },
      { content: elec.qa || "-", styles: { halign: "center" } },
      { content: elec.lab || "-", styles: { halign: "center" } },
      { content: elec.vt || "-", styles: { halign: "center" } },
      { content: elec.hvt || "-", styles: { halign: "center" } },
      { content: elec.iot || "-", styles: { halign: "center" } },
      { content: elec.ovt || "-", styles: { halign: "center" } },
      { content: elec.ost || "-", styles: { halign: "center" } },
      { content: elec.pdt || "-", styles: { halign: "center" } },
      { content: elec.it || "-", styles: { halign: "center" } },
      { content: elec.mt || "-", styles: { halign: "center" } },
      { content: elec.ft || "-", styles: { halign: "center" } },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    head: [
      [
        { content: "General Scope", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
        { content: "Quality assurance", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
        { content: "Lab tests", rowSpan: 2, styles: { halign: "center", valign: "middle" } },
        { content: "( Test On Electrical )", colSpan: 9, styles: { halign: "center" } },
      ],
      [
        { content: "VT", styles: { halign: "center" } },
        { content: "HVT", styles: { halign: "center" } },
        { content: "IOT", styles: { halign: "center" } },
        { content: "OVT", styles: { halign: "center" } },
        { content: "OST", styles: { halign: "center" } },
        { content: "PDT", styles: { halign: "center" } },
        { content: "IT", styles: { halign: "center" } },
        { content: "MT", styles: { halign: "center" } },
        { content: "FT", styles: { halign: "center" } },
      ],
    ],
    body: elecBody,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSizeSm },
    headStyles: { ...getHeaderStyles(), halign: "center" },
    columnStyles: {
      0: { cellWidth: contentW * 0.20 },
      1: { cellWidth: contentW * 0.09, halign: "center" },
      2: { cellWidth: contentW * 0.07, halign: "center" },
      3: { cellWidth: contentW * 0.07, halign: "center" },
      4: { cellWidth: contentW * 0.07, halign: "center" },
      5: { cellWidth: contentW * 0.07, halign: "center" },
      6: { cellWidth: contentW * 0.07, halign: "center" },
      7: { cellWidth: contentW * 0.07, halign: "center" },
      8: { cellWidth: contentW * 0.07, halign: "center" },
      9: { cellWidth: contentW * 0.07, halign: "center" },
      10: { cellWidth: contentW * 0.08, halign: "center" },
      11: { cellWidth: contentW * 0.07, halign: "center" },
    },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(6);

  // Table 4: LEGENDS Table
  const legendsBody = [
    [
      { content: "MECHANICAL", rowSpan: 2, styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [245, 245, 245], valign: "middle" } },
      { content: "RTFI: Radiographic Testing Film interpretation" },
      { content: "UT: Ultrasonic Testing" },
      { content: "PT: Dye Penetrant Testing" },
      { content: "MT: Magnetic Particle Testing" },
    ],
    [
      { content: "VT: Visual Examination" },
      { content: "ECT: Eddy Current Testing" },
      { content: "LT: Leak Testing" },
      { content: "VBT: Vacuum Box Testing" },
    ],
    [
      { content: "ELECTRICAL", rowSpan: 2, styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [245, 245, 245], valign: "middle" } },
      { content: "VT: Visual Examination" },
      { content: "HVT: High Voltage Test" },
      { content: "IOT: Induced Over Voltage Test" },
      { content: "OVT: Over Voltage Test" },
    ],
    [
      { content: "OST: Over Speed Test" },
      { content: "PDT: Partial Discharge Test" },
      { content: "IT: Impulse Test" },
      { content: "MT: Megger Test / FT: Flash Test" },
    ],
    [
      { content: "Level of Authorization", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [245, 245, 245] } },
      { content: "Grade A - Proficient in training, monitoring, guiding, and evaluating fellow inspectors" },
      { content: "Grade B - Competent to work autonomously and provide guidance to others" },
      { content: "Grade C - Capable of working independently under senior guidance" },
      { content: "Grade D - Able to perform tasks under supervision." },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    head: [
      [
        { content: "LEGENDS :", colSpan: 5, styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [230, 235, 245] } },
      ],
    ],
    body: legendsBody,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: 6.8 },
    columnStyles: {
      0: { cellWidth: contentW * 0.16 },
      1: { cellWidth: contentW * 0.21 },
      2: { cellWidth: contentW * 0.21 },
      3: { cellWidth: contentW * 0.21 },
      4: { cellWidth: contentW * 0.21 },
    },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(8);
};

// -----------------------------------------------------------------------------
// SUPERVISION & SIGNATURES SECTION
// -----------------------------------------------------------------------------

const drawSupervisionAndSignatures = (doc, data) => {
  const contentW = layout.contentWidth;
  const leftX = config.margin;

  const isReq = data.supervisedPeriodRequired === "Required";
  const isNotReq = data.supervisedPeriodRequired === "Not Required";

  const reqLabel = `[ ${isReq ? "X" : " "} ] Required`;
  const notReqLabel = `[ ${isNotReq ? "X" : " "} ] Not Required`;

  const interviewerContent = data.interviewerName
    ? `Sign & Name of Interviewer :  ${data.interviewerName}\n\nDate :  ${data.interviewerDate || ""}`
    : `Sign & Name of Interviewer :\n\nDate : ___________________________`;

  const managerContent = data.managerName
    ? `Sign & Name of Manager :  ${data.managerName}\n\nDate :  ${data.managerDate || ""}`
    : `Sign & Name of Manager :\n\nDate : ___________________________`;

  const superRows = [
    [
      {
        content: `Supervised working period :   ${reqLabel}       ${notReqLabel}\n\nPeriod of supervised working : ${data.supervisedPeriodDuration || "-"}\n\nScope sector : ${data.scopeSectorValue || "-"}`,
        colSpan: 2,
        styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, cellPadding: 6, fillColor: [250, 252, 255] },
      },
    ],
    [
      {
        content: interviewerContent,
        styles: { cellWidth: contentW * 0.5, minCellHeight: 44, valign: "top", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, cellPadding: 6 },
      },
      {
        content: managerContent,
        styles: { cellWidth: contentW * 0.5, minCellHeight: 44, valign: "top", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, cellPadding: 6 },
      },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    body: superRows,
    theme: "grid",
    styles: { ...getBaseStyles() },
    columnStyles: {
      0: { cellWidth: contentW * 0.5 },
      1: { cellWidth: contentW * 0.5 },
    },
    margin: { top: config.contentTop, bottom: config.contentBottom, left: leftX, right: leftX },
  });

  layout.afterTable(8);
};

// -----------------------------------------------------------------------------
// MAIN GENERATOR ENTRY POINT
// -----------------------------------------------------------------------------

export const generateInterviewPersonnelPDF = async (data = {}) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  await Promise.all([
    loadAndAddFont(doc, ArialNormal, "Arial-Normal", customFontFamily.normalFont, customFontFamily.normal),
    loadAndAddFont(doc, ArialBold, "Arial-Bold", customFontFamily.boldFont, customFontFamily.bold),
    loadAndAddFont(doc, ArialItalic, "Arial-Italic", customFontFamily.italicFont, customFontFamily.italic),
    loadAndAddFont(doc, ArialBoldItalic, "Arial-BoldItalic", customFontFamily.normalFont, "bolditalic"),
  ]);

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

  layout.init(doc);
  layout.y = config.contentTop + 6;

  drawSection1(doc, data);
  drawSection2(doc, data);
  drawSection3(doc, data);
  drawResultsAndAuthorizations(doc, data);
  drawSupervisionAndSignatures(doc, data);

  const totalPages = doc.internal.getNumberOfPages();
  addPageDecorators(doc, totalPages, data);

  return doc;
};

export default generateInterviewPersonnelPDF;
